/**
 * Phase 101fm (Feb 2026) — PDF Relevé de compte revendeur
 *
 * Alain : « Ajoute un bouton exporter le relevé de compte revendeur en PDF
 * corporate imprimable ». Aligné sur le style visuel des factures Maison
 * Thaï (couleurs cream, bandeau vert, doré en accent), avec fenêtre B5/C5
 * pour enveloppe à fenêtre.
 *
 * Contenu :
 *   • En-tête émetteur + adresse débiteur (fenêtre)
 *   • Bandeau « Solde d'ouverture / Solde de clôture » + période
 *   • Tableau chronologique : Date · Réf · Description · Débit · Crédit · Solde
 *   • Pied comptable : totaux facturé / payé / avoirs
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";

export const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)}`;
export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};
export const fmtDateFull = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
};
export const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 9, color: "#1A1C1A" },
  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  senderBlock: { flexDirection: "row", alignItems: "flex-start", gap: 10, maxWidth: "58%" },
  senderLogo: { width: 55, height: 55 },
  senderInfo: { flexDirection: "column" },
  senderName: { fontSize: 15, fontFamily: "Helvetica-Bold", marginBottom: 4, letterSpacing: 0.6 },
  senderMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  titleRight: { flexDirection: "column", alignItems: "flex-end", maxWidth: "40%" },
  titleMain: { fontSize: 20, fontFamily: "Helvetica-Bold", letterSpacing: 1.2, color: "#1F4E3D" },
  titleSub: { fontSize: 8.5, color: "#575A57", marginTop: 1, marginBottom: 6 },
  titleRef: { fontSize: 10, fontFamily: "Helvetica-Bold", marginTop: 2 },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 1 },

  // Fenêtre C5/B5 pour adresse débiteur (standard suisse)
  debtorWindow: {
    position: "absolute",
    top: "48mm", left: "115mm",
    width: "85mm", height: "45mm",
    padding: 4,
  },
  debtorName: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  debtorLine: { fontSize: 9.5, color: "#1A1C1A", lineHeight: 1.35 },

  // Espace après fenêtre débiteur
  postWindow: { marginTop: "60mm" },

  // Bandeau période + soldes (2 boîtes côte à côte)
  bandRow: { flexDirection: "row", gap: 12, marginBottom: 14 },
  bandBoxLeft: { flex: 1, padding: 10, borderLeft: "3pt solid #C1A063", backgroundColor: "#FBF4E9" },
  bandBoxRight: { flex: 1, padding: 10, borderLeft: "3pt solid #1F4E3D", backgroundColor: "#EAF4EE" },
  bandLabel: { fontSize: 7, color: "#A3A6A3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },
  bandValue: { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#1A1C1A" },
  bandSub: { fontSize: 8.5, color: "#575A57", marginTop: 2 },

  // Table
  table: { borderTop: "1pt solid #E5E5E0", borderLeft: "1pt solid #E5E5E0", borderRight: "1pt solid #E5E5E0", marginBottom: 6 },
  th: { flexDirection: "row", backgroundColor: "#F4F4F0", padding: 6, borderBottom: "1pt solid #E5E5E0" },
  thCell: { fontFamily: "Helvetica-Bold", fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  tr: { flexDirection: "row", padding: 6, borderBottom: "0.5pt solid #E5E5E0", alignItems: "flex-start" },
  td: { fontSize: 9 },
  trOpening: { flexDirection: "row", padding: 6, borderBottom: "1pt solid #A3A6A3", backgroundColor: "#FAFAF7" },
  tdOpening: { fontSize: 9, fontFamily: "Helvetica-Bold" },
  trClosing: { flexDirection: "row", padding: 6, borderTop: "1pt solid #1F4E3D", backgroundColor: "#F4F4F0" },
  tdClosing: { fontSize: 10, fontFamily: "Helvetica-Bold" },

  // Colonnes largeur
  colDate: { width: "12%" },
  colType: { width: "14%" },
  colRef: { width: "18%" },
  colDesc: { flex: 1 },
  colAmount: { width: "12%", textAlign: "right" },

  // Résumé pied
  summary: { alignSelf: "flex-end", width: "55%", marginTop: 10 },
  summaryRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  summaryLabel: { fontSize: 9, color: "#575A57" },
  summaryValue: { fontSize: 9, fontFamily: "Helvetica-Bold" },

  // Signature / mention
  legal: { marginTop: 24, fontSize: 8, color: "#575A57", lineHeight: 1.4 },
  legalBold: { fontFamily: "Helvetica-Bold" },

  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

const TYPE_LABEL = {
  INVOICE: "Facture",
  PAYMENT: "Paiement",
  CREDIT_NOTE: "Avoir",
};

export default function ResellerStatementPdf({ statement, settings, logoDataUrl }) {
  if (!statement || !statement.reseller) return null;
  const r = statement.reseller;
  const entries = statement.entries || [];
  const totals = statement.totals || {};

  const senderAddress = (settings?.company_address || "").split("\n").filter(Boolean);
  const senderZip = settings?.company_postal_code || "";
  const senderCity = settings?.company_city || "";
  const senderPhone = settings?.company_phone || "";
  const senderEmail = settings?.company_email || "";
  const senderVat = settings?.company_vat_number || "";
  const finalLogo = logoDataUrl || settings?.company_logo_url;

  const debtorLines = [
    r.contact_name,
    r.address,
    `${r.postal_code || ""} ${r.city || ""}`.trim(),
  ].filter(Boolean);

  const generatedAt = new Date().toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header émetteur + titre */}
        <View style={styles.headerRow}>
          <View style={styles.senderBlock}>
            {finalLogo && <Image src={finalLogo} style={styles.senderLogo} />}
            <View style={styles.senderInfo}>
              <Text style={styles.senderName}>{COMPANY_HEADER}</Text>
              {senderAddress.map((line, i) => (
                <Text key={i} style={styles.senderMeta}>{line}</Text>
              ))}
              {(senderZip || senderCity) && (
                <Text style={styles.senderMeta}>CH-{senderZip} {senderCity}</Text>
              )}
              {senderPhone && <Text style={styles.senderMeta}>Tél {senderPhone}</Text>}
              {senderEmail && <Text style={styles.senderMeta}>{senderEmail}</Text>}
              {senderVat && <Text style={styles.senderMeta}>TVA : {senderVat}</Text>}
            </View>
          </View>
          <View style={styles.titleRight}>
            <Text style={styles.titleMain}>RELEVÉ</Text>
            <Text style={styles.titleSub}>Compte client</Text>
            <Text style={styles.titleRef}>Édité le {generatedAt}</Text>
            <Text style={styles.titleDate}>Période du {fmtDate(statement.from_date)} au {fmtDate(statement.to_date)}</Text>
          </View>
        </View>

        {/* Adresse débiteur en fenêtre */}
        <View style={styles.debtorWindow}>
          <Text style={styles.debtorName}>{r.name}</Text>
          {debtorLines.map((l, i) => <Text key={i} style={styles.debtorLine}>{l}</Text>)}
        </View>

        <View style={styles.postWindow} />

        {/* Bandeau soldes */}
        <View style={styles.bandRow}>
          <View style={styles.bandBoxLeft}>
            <Text style={styles.bandLabel}>Solde d'ouverture</Text>
            <Text style={styles.bandValue}>{fmtCHF(statement.opening_balance)} CHF</Text>
            <Text style={styles.bandSub}>au {fmtDateFull(statement.from_date)}</Text>
          </View>
          <View style={styles.bandBoxRight}>
            <Text style={styles.bandLabel}>Solde de clôture</Text>
            <Text style={[styles.bandValue, {
              color: statement.closing_balance > 0.01 ? "#B24A32" : statement.closing_balance < -0.01 ? "#3E5C34" : "#1A1C1A"
            }]}>
              {fmtCHF(statement.closing_balance)} CHF
            </Text>
            <Text style={styles.bandSub}>
              {statement.closing_balance > 0.01 ? "Doit à Maison Thaï" : statement.closing_balance < -0.01 ? "Avoir en votre faveur" : "Compte à jour"}
            </Text>
          </View>
        </View>

        {/* Table mouvements */}
        <View style={styles.table}>
          <View style={styles.th}>
            <Text style={[styles.thCell, styles.colDate]}>Date</Text>
            <Text style={[styles.thCell, styles.colType]}>Type</Text>
            <Text style={[styles.thCell, styles.colRef]}>Référence</Text>
            <Text style={[styles.thCell, styles.colDesc]}>Description</Text>
            <Text style={[styles.thCell, styles.colAmount]}>Débit</Text>
            <Text style={[styles.thCell, styles.colAmount]}>Crédit</Text>
            <Text style={[styles.thCell, styles.colAmount]}>Solde</Text>
          </View>

          {/* Ligne solde ouverture */}
          <View style={styles.trOpening}>
            <Text style={[styles.tdOpening, styles.colDate]}>{fmtDate(statement.from_date)}</Text>
            <Text style={[styles.tdOpening, styles.colType]}></Text>
            <Text style={[styles.tdOpening, styles.colRef]}></Text>
            <Text style={[styles.tdOpening, styles.colDesc]}>Solde reporté</Text>
            <Text style={[styles.tdOpening, styles.colAmount]}></Text>
            <Text style={[styles.tdOpening, styles.colAmount]}></Text>
            <Text style={[styles.tdOpening, styles.colAmount]}>{fmtCHF(statement.opening_balance)}</Text>
          </View>

          {entries.length === 0 && (
            <View style={styles.tr}>
              <Text style={{ ...styles.td, textAlign: "center", flex: 1, color: "#A3A6A3" }}>
                Aucun mouvement enregistré sur la période
              </Text>
            </View>
          )}

          {entries.map((e, idx) => (
            <View key={idx} style={styles.tr}>
              <Text style={[styles.td, styles.colDate]}>{fmtDate(e.date)}</Text>
              <Text style={[styles.td, styles.colType]}>{TYPE_LABEL[e.type] || e.type}</Text>
              <Text style={[styles.td, styles.colRef, { fontFamily: "Helvetica-Bold" }]}>{e.reference}</Text>
              <Text style={[styles.td, styles.colDesc]}>{e.description}</Text>
              <Text style={[styles.td, styles.colAmount]}>{e.debit > 0 ? fmtCHF(e.debit) : ""}</Text>
              <Text style={[styles.td, styles.colAmount, { color: "#3E5C34" }]}>{e.credit > 0 ? `- ${fmtCHF(e.credit)}` : ""}</Text>
              <Text style={[styles.td, styles.colAmount]}>{fmtCHF(e.running_balance)}</Text>
            </View>
          ))}

          {/* Ligne solde clôture */}
          <View style={styles.trClosing}>
            <Text style={[styles.tdClosing, styles.colDate]}>{fmtDate(statement.to_date)}</Text>
            <Text style={[styles.tdClosing, styles.colType]}></Text>
            <Text style={[styles.tdClosing, styles.colRef]}></Text>
            <Text style={[styles.tdClosing, styles.colDesc]}>Solde à date</Text>
            <Text style={[styles.tdClosing, styles.colAmount]}></Text>
            <Text style={[styles.tdClosing, styles.colAmount]}></Text>
            <Text style={[styles.tdClosing, styles.colAmount, {
              color: statement.closing_balance > 0.01 ? "#B24A32" : statement.closing_balance < -0.01 ? "#3E5C34" : "#1A1C1A",
            }]}>
              {fmtCHF(statement.closing_balance)}
            </Text>
          </View>
        </View>

        {/* Résumé période */}
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total facturé sur la période</Text>
            <Text style={styles.summaryValue}>{fmtCHF(totals.invoices)} CHF</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total payé sur la période</Text>
            <Text style={[styles.summaryValue, { color: "#3E5C34" }]}>- {fmtCHF(totals.payments)} CHF</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total avoirs sur la période</Text>
            <Text style={[styles.summaryValue, { color: "#8B3A1F" }]}>- {fmtCHF(totals.credit_notes)} CHF</Text>
          </View>
        </View>

        {/* Mention légale */}
        <View style={styles.legal}>
          <Text style={styles.legalBold}>Note :</Text>
          <Text>
            Ce relevé récapitule les factures émises, les paiements reçus et les avoirs enregistrés sur la période indiquée.
            En cas de désaccord, merci de nous contacter dans les 30 jours suivant la réception à l'adresse indiquée en en-tête.
          </Text>
        </View>

        <Text style={styles.footer}>
          Relevé de compte {r.name} · Période {fmtDate(statement.from_date)} — {fmtDate(statement.to_date)} · Édité le {generatedAt}
        </Text>
      </Page>
    </Document>
  );
}
